import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Autocomplete,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import type { CreateBatchFormData } from './validation';
import { Farmer } from '../../../types/farmers';
import { Farm } from '../../../types/farms';
import FarmerDialog from '../../Farmers/FarmerDialog';
import FarmDialog from '../../Farms/FarmDialog';

interface StandaloneWorkflowSectionProps {
  /** The react-hook-form control object */
  control: Control<CreateBatchFormData>;
  /** The form field errors */
  errors: FieldErrors<CreateBatchFormData>;
  /** List of available farmers */
  farmers: Farmer[];
  /** List of available farms (filtered by selected farmer) */
  farms: Farm[];
  /** Whether farmers are currently loading */
  loadingFarmers: boolean;
  /** Whether farms are currently loading */
  loadingFarms: boolean;
  /** Whether the form is being submitted */
  disabled: boolean;
  /** Whether this section is visible (standalone mode is active) */
  visible: boolean;
  /** Currently selected farmer ID (used to enable/disable farm selection) */
  selectedFarmerId: number | null;
  /** Callback when a new farmer is created via the inline dialog */
  onFarmerCreated?: (farmer: Farmer) => void;
  /** Callback when a new farm is created via the inline dialog */
  onFarmCreated?: (farm: Farm) => void;
}

/**
 * StandaloneWorkflowSection Component
 *
 * A form section for creating standalone workflows without linking to an existing batch.
 * Includes fields for:
 * - Farmer selection (required)
 * - Farm selection (optional, filtered by farmer)
 * - Internal Reference (optional)
 */
export const StandaloneWorkflowSection: React.FC<StandaloneWorkflowSectionProps> = ({
  control,
  errors,
  farmers,
  farms,
  loadingFarmers,
  loadingFarms,
  disabled,
  visible,
  selectedFarmerId,
  onFarmerCreated,
  onFarmCreated,
}) => {
  const [openDialog, setOpenDialog] = useState<'farmer' | 'farm' | null>(null);

  return (
    <Collapse in={visible}>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" color="primary" gutterBottom>
          Workflow Details
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create a standalone workflow. A batch can be linked later when samples are received.
        </Typography>
        <Grid container spacing={2}>
          {/* Farmer Selection */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Controller
                name="farmerId"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    sx={{ flex: 1 }}
                    value={farmers.find((f) => f.idFarmer === field.value) || null}
                    onChange={(_, newValue) => field.onChange(newValue?.idFarmer || null)}
                    options={farmers}
                    getOptionLabel={(option) =>
                      option.fullName || `${option.firstName} ${option.surname || ''}`
                    }
                    loading={loadingFarmers}
                    disabled={disabled}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Farmer"
                        required
                        error={!!errors.farmerId}
                        helperText={
                          (errors.farmerId?.message as string) ||
                          'Select the farmer for this workflow'
                        }
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingFarmers ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key: _key, ...rest } = props;
                      return (
                        <li key={option.idFarmer} {...rest}>
                          <Box>
                            <Typography variant="body2">
                              {option.fullName || `${option.firstName} ${option.surname || ''}`}
                            </Typography>
                            {option.emailAddress && (
                              <Typography variant="caption" color="text.secondary">
                                {option.emailAddress}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      );
                    }}
                  />
                )}
              />
              <Tooltip title="Create new farmer">
                <span>
                  <IconButton
                    onClick={() => setOpenDialog('farmer')}
                    disabled={disabled}
                    color="primary"
                    sx={{ mt: 1 }}
                  >
                    <AddIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Grid>

          {/* Farm Selection */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Controller
                name="farmId"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    sx={{ flex: 1 }}
                    value={farms.find((f) => f.idFarm === field.value) || null}
                    onChange={(_, newValue) => field.onChange(newValue?.idFarm || null)}
                    options={farms}
                    getOptionLabel={(option) => option.farmName}
                    loading={loadingFarms}
                    disabled={disabled || !selectedFarmerId}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Farm"
                        required
                        error={!!errors?.farmId}
                        helperText={
                          errors?.farmId?.message as string ||
                          (!selectedFarmerId
                            ? 'Select a farmer first'
                            : 'Select the farm for this workflow')
                        }
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingFarms ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      const { key: _key, ...rest } = props;
                      return (
                        <li key={option.idFarm} {...rest}>
                          <Box>
                            <Typography variant="body2">{option.farmName}</Typography>
                            {option.country && (
                              <Typography variant="caption" color="text.secondary">
                                {option.country}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      );
                    }}
                  />
                )}
              />
              <Tooltip title="Create new farm">
                <span>
                  <IconButton
                    onClick={() => setOpenDialog('farm')}
                    disabled={disabled || !selectedFarmerId}
                    color="primary"
                    sx={{ mt: 1 }}
                  >
                    <AddIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Grid>

          {/* Internal Reference */}
          <Grid size={{ xs: 12 }}>
            <Controller
              name="clientReference"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Internal Reference"
                  fullWidth
                  disabled={disabled}
                  helperText="Optional internal reference"
                />
              )}
            />
          </Grid>

        </Grid>
      </Paper>

      <FarmerDialog
        open={openDialog === 'farmer'}
        farmer={null}
        onClose={() => setOpenDialog(null)}
        onCreated={(farmer) => {
          setOpenDialog(null);
          onFarmerCreated?.(farmer);
        }}
      />
      <FarmDialog
        open={openDialog === 'farm'}
        farm={null}
        preselectedFarmerId={selectedFarmerId || undefined}
        onClose={() => setOpenDialog(null)}
        onCreated={(farm) => {
          setOpenDialog(null);
          onFarmCreated?.(farm);
        }}
      />
    </Collapse>
  );
};

export default StandaloneWorkflowSection;
